<script lang="ts">
	import ProductB2bCard from "@components/ProductB2bCard.svelte";
	import type { ProductForCard } from "@components/ProductCard.js";
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import { ButtonVariant } from "@components/common/ButtonVariant.js";
	import Formatted from "@components/common/Formatted.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
	import { getWeightText } from "@core/schema/utils/getWeightText.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./ProductB2bListingGrid.translate.json" with { type: "json" };

	const {
		products,
		subcategories,
	}: {
		products: ProductForCard[];
		subcategories: SubcategoryForCustomer[];
	} = $props();

	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);

	const productCountsByWeightInGrams = $derived(
		Cart.orderProducts.reduce<Record<number, number>>((acc, orderProduct) => {
			if (orderProduct.info.variantType !== ProductVariantType.Weight) {
				return acc;
			}
			const { weightInGrams } = orderProduct.info.variant;
			acc[weightInGrams] = (acc[weightInGrams] ?? 0) + orderProduct.quantity;
			return acc;
		}, {}),
	);
</script>

<div class="grid grid-cols-1 lg:grid-cols-[auto_repeat(3,min-content)] gap-x-16 gap-y-4">
	{#if products.length > 0}
		<div class="hidden lg:grid grid-cols-subgrid col-span-full text-fcrgray size-4 font-medium">
			<div class="pl-6"><Formatted text={translate("product")} /></div>
			<div class="text-right"><Formatted text={translate("variant")} /></div>
			<div><Formatted text={translate("quantity")} /></div>
			<div class="text-right pr-10"><Formatted text={translate("price")} /></div>
		</div>
	{/if}

	{#each products as product (product)}
		{@const subcategory = subcategories.find(({ id }) => id === product.subcategoryId)}
		{#if subcategory}
			<ProductB2bCard {product} {subcategory} />
		{/if}
	{:else}
		<div class="text-fcrdarkgray col-span-full text-center my-5">
			<Formatted text={translate("noProducts")} />
		</div>
	{/each}
</div>
{#if Cart.itemCount > 0}
	<div
		class="fixed bottom-0 inset-x-0 z-20 grid-container bg-white shadow-[0_0.25rem_3rem_0_rgba(0,0,0,0.08)] border-t border-fcrlightgray-300"
	>
		<div class="flex items-center justify-between py-2 gap-2">
			<div class="flex items-center gap-2 lg:gap-5 font-antipol text-4.5/normal">
				<div class="text-fcrgray font-medium hidden md:block">
					<Formatted text={translate("selected")} />
				</div>
				{#each Object.entries(productCountsByWeightInGrams) as [weightInGrams, count] (weightInGrams)}
					<div class="font-bold">
						<span class="text-fcrbrown"><Formatted text={`${count}x`} /></span>
						<span class="text-fcrdarkgray"
							><Formatted text={getWeightText(tenant.locale, parseInt(weightInGrams, 10))} /></span
						>
					</div>
				{/each}
			</div>
			<div class="hidden lg:block">
				<Button
					disabled={Cart.itemCount === 0}
					variant={ButtonVariant.Dark}
					size={ButtonSize.Large}
					onclick={(): void => {
						Cart.showModal = true;
					}}
				>
					<Formatted text={translate("continueToCart")} />
				</Button>
			</div>
			<div class="block lg:hidden">
				<Button
					disabled={Cart.itemCount === 0}
					variant={ButtonVariant.Dark}
					size={ButtonSize.Medium}
					onclick={(): void => {
						Cart.showModal = true;
					}}
				>
					<Formatted text={translate("continueToCartShort")} />
				</Button>
			</div>
		</div>
	</div>
{/if}
